import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, ArrowRight, Loader2, CheckCircle, Mail, AlertCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import { trackEvent } from '../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CreateAccount() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  
  // Get email and tier from URL params (from payment success redirect)
  const emailFromUrl = decodeURIComponent(params.get('email') || '');
  const tier = params.get('tier') || 'self_serve';
  
  const [email, setEmail] = useState(emailFromUrl);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Tier display names
  const tierNames = {
    'self_serve': 'Self Serve',
    'kickstart': 'Relocation Kickstart',
    'relocation_kickstart': 'Relocation Kickstart',
    'full_coordination': 'Full Coordination',
    'concierge': 'Full Coordination'
  };
  const tierName = tierNames[tier] || 'Self Serve';

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/portal/dashboard');
    }
  }, [user, navigate]);

  // Update email from URL if it changes
  useEffect(() => {
    if (emailFromUrl) {
      setEmail(emailFromUrl);
    }
  }, [emailFromUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!email) {
      setError('Email is required');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    
    try {
      // Create account with password
      const response = await fetch(`${API_URL}/api/auth/complete-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, confirm_password: confirmPassword })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create account');
      }
      
      setSuccess(true);

      // iter 131 - GA4 funnel completion. Fires once per successful
      // password-save / account-creation, before the portal redirect.
      // Combined with `view_blueprint` + `nav_cta_click` + Stripe's
      // `purchase` event, this closes the homepage → portal funnel.
      trackEvent('blueprint_purchase_completed', {
        tier: tier || 'unknown',
        email_domain: (email.split('@')[1] || '').toLowerCase(),
      });

      // Auto-login after successful account creation
      const loginResult = await login(email, password);
      
      if (loginResult.success) {
        // Redirect to portal after brief success message
        setTimeout(() => {
          navigate('/portal/dashboard');
        }, 1500);
      } else {
        // If auto-login fails, still show success and let user try manually
        setTimeout(() => {
          navigate('/portal');
        }, 2000);
      }
      
    } catch (err) {
      console.error('Account creation error:', err);
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-[#F9F6EF]">
        <SEO title="Account Created - Welcome to Your Portal" />
        <Header />
        <section className="pt-[140px] sm:pt-[180px] pb-24 px-4 sm:px-6">
          <div className="max-w-md mx-auto text-center">
            <div className="w-16 h-16 rounded-full bg-[#2CA8A0]/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-[#2CA8A0]" />
            </div>
            <h1 className="text-2xl text-[#0A1F2E] font-light mb-2">Account Created!</h1>
            <p className="text-[#5A6B73] mb-4">Taking you to your portal...</p>
            <Loader2 className="w-6 h-6 text-[#2CA8A0] animate-spin mx-auto" />
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F6EF]">
      <SEO 
        title="Set Your Password - Access Your Portal" 
        description="Create your password to access your Client Portal and Blueprint." 
      />
      <Header />

      <section className="pt-[140px] sm:pt-[180px] pb-24 px-4 sm:px-6">
        <div className="max-w-md mx-auto">

          {/* iter 130 - Back link top-left */}
          <Link
            to="/"
            data-testid="back-to-mundalo-link"
            className="inline-block text-[#5A6B73] text-sm hover:text-[#0A1F2E] mb-8 transition-colors"
          >
            ← Back to mundalo.com
          </Link>

          {/* Success Badge */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <CheckCircle className="w-5 h-5 text-[#2CA8A0]" />
            <span className="text-[#2CA8A0] text-sm font-medium">Payment Successful</span>
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            {/* iter 130 - Progress eyebrow */}
            <p
              data-testid="progress-eyebrow"
              className="font-mono uppercase text-[11px] tracking-[0.15em] text-[#2CA8A0] mb-3"
            >
              You're 30 seconds from your portal
            </p>
            <h1 className="text-2xl sm:text-3xl text-[#0A1F2E] font-light mb-2">
              Set Your Password
            </h1>
            <p className="text-[#5A6B73] text-sm sm:text-base">
              Create a password to access your Client Portal and {tierName} benefits.
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white border border-[#d5d0c8] rounded-lg shadow-sm p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              
              {/* Email Field (Pre-filled) */}
              <div>
                <label className="block text-[#5A6B73] text-sm mb-2">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5A6B73]" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`w-full bg-[#F9F6EF] border border-[#d5d0c8] text-[#0A1F2E] pl-11 pr-4 py-3 text-base focus:border-[#2CA8A0] focus:outline-none transition ${emailFromUrl ? 'text-[#5A6B73]' : ''}`}
                    placeholder="your@email.com"
                    readOnly={!!emailFromUrl}
                    data-testid="email-field"
                  />
                </div>
                {emailFromUrl && (
                  <p className="text-[#5A6B73] text-xs mt-1">This is the email from your purchase</p>
                )}
              </div>

              {/* Create Password Field */}
              <div>
                <label className="block text-[#5A6B73] text-sm mb-2">Create Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5A6B73]" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-[#F9F6EF] border border-[#d5d0c8] text-[#0A1F2E] pl-11 pr-12 py-3 text-base focus:border-[#2CA8A0] focus:outline-none transition"
                    placeholder="Minimum 8 characters"
                    minLength={8}
                    required
                    data-testid="password-field"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A6B73] hover:text-[#0A1F2E] transition"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password Field */}
              <div>
                <label className="block text-[#5A6B73] text-sm mb-2">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5A6B73]" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`w-full bg-[#F9F6EF] border text-[#0A1F2E] pl-11 pr-4 py-3 text-base focus:outline-none transition ${
                      confirmPassword && password !== confirmPassword 
                        ? 'border-red-500 focus:border-red-500' 
                        : confirmPassword && password === confirmPassword 
                          ? 'border-[#2CA8A0] focus:border-[#2CA8A0]' 
                          : 'border-[#d5d0c8] focus:border-[#2CA8A0]'
                    }`}
                    placeholder="Re-enter your password"
                    minLength={8}
                    required
                    data-testid="confirm-password-field"
                  />
                  {confirmPassword && password === confirmPassword && (
                    <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#2CA8A0]" />
                  )}
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-red-400 text-xs mt-1">Passwords don't match</p>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || password.length < 8 || password !== confirmPassword}
                className="w-full bg-[#E8A830] hover:brightness-95 text-[#0A1F2E] font-semibold py-4 text-base rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    Save Password & Access Portal
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Help Text */}
          <p className="text-center text-[#5A6B73] text-xs mt-6">
            Already have an account?{' '}
            <a href="/portal" className="text-[#2CA8A0] hover:underline">
              Sign in here
            </a>
          </p>

        </div>
      </section>

      <Footer />
    </div>
  );
}
